import { Ability } from '@casl/ability';
import {
  Actions,
  CertificationAndLicense,
  CertsAndLicenseEdit,
  CertsAndLicenseHookResponse,
  ResourceForm,
  ResultStatus,
  SelectedCertificationsAndLicenses,
  Subjects,
  UpsertResource,
} from '@dispatch/shared/types';
import { SectionWithMeta } from '@dispatch/web/ui';
import { Box } from '@mui/material';
import React, { useContext } from 'react';
import { ResourceContext } from '../../ResourcesDetails';
import { AssignCertOrLicense } from './AssignCertOrLicense';
import CertsAndLicensesList from './CertsAndLicensesList';
import { UseFormSetValue } from 'react-hook-form';
import { updateCertsAndLicenses } from '@dispatch/shared';

export function CertsAndLicensesTab({
  certsAndLicenseResponse,
  upsertResource,
  upsertResourceStatus,
  permissions,
  setValue,
  isReadOnly = false,
}: {
  certsAndLicenseResponse?: CertificationAndLicense[];
  upsertResource: UpsertResource;
  upsertResourceStatus?: ResultStatus;
  permissions: Ability<[Actions, Subjects]> | undefined;
  setValue: UseFormSetValue<ResourceForm>;
  isReadOnly?: boolean;
}) {
  const { data, upsertResourceStatus: status } = useContext(ResourceContext);
  const [editData, setEditData] = React.useState<CertsAndLicenseEdit | null>(
    null
  );

  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');

  const onDeleteLicOrCert = (licenseNumber: string) => {
    const postBody = {
      ...data,
      selectedCertificationsAndLicenses:
        data?.selectedCertificationsAndLicenses.filter(
          (license: SelectedCertificationsAndLicenses) =>
            license.licenseNumber !== licenseNumber
        ),
    };

    setValue(
      'certsAndLicenses',
      updateCertsAndLicenses(postBody.selectedCertificationsAndLicenses)
    );

    upsertResource(postBody);
  };

  const onEditRow = (row: SelectedCertificationsAndLicenses) => {
    const companyLicense = certsAndLicenseResponse?.find(
      (license: CertificationAndLicense) =>
        license.integrationGUID === row.certificationAndLicenseIntegrationGUID
    );

    setEditData({
      resourceCertId: row?.integrationGUID,
      name: companyLicense?.name || '',
      organization: companyLicense?.organization || '',
      description: companyLicense?.description || '',
      stateProvince: companyLicense?.stateProvince || [],
      verificationType: companyLicense?.verificationType || '',
      issueDate: row.issueDate,
      expirationDate: row.expirationDate || '',
      licenseNumber: row.licenseNumber,
      integrationGUID: companyLicense?.integrationGUID || '',
      mode: 'edit',
    });
  };

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <SectionWithMeta
        title="Assigned Certifications and Licenses to Adjuster"
        meta={() => (
          <Box hidden={!hasUpsertPermissions && !isReadOnly}>
            <AssignCertOrLicense
              resource={data}
              certsAndLicense={certsAndLicenseResponse || []}
              upsertResource={upsertResource}
              upsertResourceStatus={upsertResourceStatus}
              editState={{ editData, setEditData }}
              setValue={setValue}
              isReadOnly={isReadOnly}
            />
          </Box>
        )}
      >
        <CertsAndLicensesList
          certsAndLicense={certsAndLicenseResponse || []}
          isLoading={status === 'loading'}
          onEditRow={onEditRow}
          onDeleteLicOrCert={onDeleteLicOrCert}
          isReadOnly={isReadOnly}
        />
      </SectionWithMeta>
    </div>
  );
}
